import { SyntheticEvent } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { UseSortByColumnOptions, UseTableColumnOptions } from 'react-table';
import { TablePagination } from './TablePagination';
import { HeadCell } from './HeadCell';
import { EmptyRow, Row } from './Row';
import { Trans } from 'react-i18next';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  container: {
    position: 'relative',
    backgroundColor: theme.colors.white,
    padding: [[0, theme.marginBase / 2]],

    borderRadius: theme.borderRadius.std,
    overflow: 'hidden',

    width: '100%',
    paddingBottom: theme.height.large, // for the footer
    [theme.media.mobile]: {
      paddingBottom: 0,
    },
  },
  tableContainer: {
    backgroundColor: theme.colors.white,

    overflowX: 'auto',
    width: '100%',

    [theme.media.mobile]: {
      overflowX: 'hidden',
      backgroundColor: 'transparent',
      padding: 0,
      minHeight: 0,
      display: 'block',
    },
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  thead: {
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: theme.colors.grayBorder,

    [theme.media.mobile]: {
      display: 'none',
    },
  },
  tableFooter: {
    ...theme.fonts.label,

    backgroundColor: theme.colors.white,

    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,

    height: theme.height.large,

    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    borderTop: {
      width: 1,
      style: 'solid',
      color: theme.colors.grayBorder,
    },

    [theme.media.mobile]: {
      position: 'relative',
      borderTopWidth: 0,
    },
  },
  numberOfElements: {
    paddingLeft: theme.marginBase * 3,
  },
}));

type TableColumnOptions<D extends object> = UseTableColumnOptions<D> &
  UseSortByColumnOptions<D>;

export interface ColumnOptions<D extends object> extends TableColumnOptions<D> {
  notOrderable?: boolean;
  singleLine?: boolean;
}

interface Props {
  // tableInfos: UseTableInstanceProps<D> &
  //   UsePaginationInstanceProps<D> & { state: any };
  tableInfos: any;
  count: number;
  onRowClick?: (id: string, e: SyntheticEvent) => any;
  selectedId?: string;
  className?: string;
}

export const DataTable = ({
  tableInfos: {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageCount,
    gotoPage,
    state,
    rows,
  },
  count,
  onRowClick,
  selectedId,
  className,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const pageSize = state && state.pageSize ? state.pageSize : 10;
  const emptyRowsArray = new Array(pageSize - page.length).fill(0);

  // Render the UI for your table
  return (
    <div className={classnames(classes.container, className)}>
      <div className={classes.tableContainer}>
        <table {...getTableProps()} className={classes.table}>
          <thead className={classes.thead}>
            {!!count &&
              headerGroups.map((headerGroup: any) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any) => (
                    <HeadCell column={column} key={column.id}>
                      {column.render('Header')}
                    </HeadCell>
                  ))}
                </tr>
              ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row: any) => {
              prepareRow(row);
              return (
                <Row
                  row={row}
                  onClick={onRowClick}
                  key={row.id}
                  selected={row.isSelected}
                  lineClicked={row.id === selectedId}
                />
              );
            })}
            {emptyRowsArray.map((_, key) => (
              <EmptyRow key={key} />
            ))}
          </tbody>
        </table>
      </div>
      <footer className={classes.tableFooter}>
        <div className={classes.numberOfElements}>
          {count !== rows.length ? (
            <Trans
              i18nKey="participants.list.totalFiltered"
              values={{ count: rows.length, total: count }}
            />
          ) : (
            <Trans i18nKey="participants.list.total" values={{ count }} />
          )}
        </div>
        <TablePagination
          pageCount={pageCount}
          gotoPage={gotoPage}
          page={state.pageIndex}
        />
      </footer>
    </div>
  );
};
